import {
  Box,
  Card,
  LinearProgress,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import dayjs from 'dayjs'
import React from 'react'

import { fetchAptOverview, fetchAptRequests } from 'api/apartmentProcurement'

import {
  aptAttentionRows,
  aptBudgetRows,
  aptCategorySpend,
  aptFlowCounts,
  aptKitRows,
  kitsIncomplete,
  todayStr
} from './actionSummary'
import { useAptCategories } from './aptCategories'
import { apiErrorText, money } from './constants'

// The At Apartment board, re-cut as a picture (Satya, 2026-08-02). It replaces
// the six flat count cards: one proportional strip for the whole pipeline, then
// budget, kit, chase-list and category detail underneath. Read-only by design —
// the Budgets tab keeps the editable tables, this tab only says where things
// stand and clicks through to the tab where the work happens.
//
// Same two calls the old cards made — fetchAptRequests({origin:'apartment',
// lines:'1'}) and fetchAptOverview(). No new endpoint, no migration. All the
// arithmetic lives in actionSummary.js so it stays testable and so the strip
// can never disagree with the numbers elsewhere on the page.

// Warmed neutrals + a single-hue stage ramp (early -> late). Alfred orange is
// reserved for "this one is yours to move", never used as a series colour.
const C = {
  r1: '#d6e4ef',
  r2: '#a9c8e0',
  r3: '#7baad0',
  r4: '#4e88b8',
  r5: '#2e6390',
  track: '#ece7e2',
  accent: '#f25e35',
  accentSoft: '#fdeae4',
  accentInk: '#b83f1c',
  good: '#3f8f5b',
  warn: '#c97c1f',
  warnSoft: '#fbf0dd',
  crit: '#c1443c',
  critSoft: '#fbe8e6',
  line: '#e6e0da',
  lineSoft: '#efeae5',
  ink: '#23262c',
  ink2: '#5c6169',
  ink3: '#8b9099'
}

// The six buckets of the flow strip, in pipeline order. `onDark` decides the
// count colour: the two pale early segments carry ink, the four darker ones
// carry white with a shadow. `tab` is where that stage's work gets done.
//
// NOTE (flagged 2026-08-02): stage 4 is called "In transit" here, while
// FULFILMENT_META.dispatched in ./constants labels the same enum value
// "Received by HQ procurement team". Same data, two stories — the board uses
// the team's words, the line-level chips keep HQ's.
const STAGES = [
  {
    key: 'awaitingApproval',
    label: 'Awaiting approval',
    who: 'Cluster manager · Sounak',
    detail: 'With the cluster manager or Sounak',
    aria: 'awaiting approval',
    color: C.r1,
    onDark: false,
    tab: 'approvals'
  },
  {
    key: 'withHq',
    label: 'With HQ — not ordered',
    who: 'HQ procurement',
    detail: 'Approved, on HQ’s daily sheet, no vendor yet',
    aria: 'with HQ not ordered',
    color: C.r2,
    onDark: false,
    tab: 'requests'
  },
  {
    key: 'sentToVendor',
    label: 'Sent to vendor',
    who: 'Vendor',
    detail: 'Ordered — waiting on the vendor',
    aria: 'sent to vendor',
    color: C.r3,
    onDark: true,
    tab: 'requests'
  },
  {
    key: 'inTransit',
    label: 'In transit',
    who: 'Vendor · dispatched',
    detail: 'Dispatched — on the way to the apartment',
    aria: 'in transit',
    color: C.r4,
    onDark: true,
    tab: 'requests'
  },
  {
    key: 'confirmReceipt',
    label: 'Delivered — confirm',
    tipLabel: 'Delivered — confirm receipt',
    who: 'Confirm line by line',
    detail: 'Waiting on us to confirm line by line',
    aria: 'delivered awaiting our confirmation',
    color: C.r5,
    onDark: true,
    tab: 'requests',
    tone: 'yours',
    tag: 'Your move'
  },
  {
    key: 'flagged',
    label: 'Flagged',
    who: 'Short, wrong, not received',
    detail: 'Short, wrong or not received — HQ following up',
    aria: 'flagged',
    color: C.crit,
    onDark: true,
    tab: 'requests',
    tone: 'problem',
    tag: 'Blocked'
  }
]

// The two legend cells that are not just information: one is ours to clear,
// one is blocked. Everything else keeps the plain rail.
const LEGEND_TONE = {
  yours: { rail: C.accent, background: C.accentSoft, tag: C.accent },
  problem: { rail: C.crit, background: C.critSoft, tag: C.crit }
}

// The category card's header claims a TOTAL, so it has to say what is in it.
// The six budget heads come straight from the overview (the Budgets tab's own
// figures); anything HQ booked to a daily-sheet category outside those heads is
// invisible to that payload and is added back as its own bar — see
// aptCategorySpend. Saying "the same basis as the Budgets tab" would now be
// wrong: no budget head covers the Other bar.
const CATEGORY_TIP =
  'Spend that cleared the full approval chain, GST-excluded. The six budget ' +
  'heads are the Budgets tab’s own figures; anything HQ booked to a category ' +
  'outside those heads is grouped as “Other categories”, which no budget ' +
  'head covers.'

// The four states the chase list can show, with the pill they wear.
const ATTENTION_META = {
  late: { label: 'Past due from vendor', fg: C.crit, bg: C.critSoft },
  discrepancy: { label: 'Discrepancy raised', fg: C.warn, bg: C.warnSoft },
  not_received: { label: 'Not received', fg: C.warn, bg: C.warnSoft },
  // Darkened accent, not #f25e35 — the brand orange fails contrast on its own
  // soft fill.
  confirm: { label: 'Confirm receipt', fg: C.accentInk, bg: C.accentSoft }
}

const useStyles = makeStyles((theme) => ({
  page: { display: 'flex', flexDirection: 'column', gap: 20 },

  // --- cards + shared type ---------------------------------------------
  card: {
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: '18px 18px 20px'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14
  },
  h2: { fontSize: 14, fontWeight: 700, letterSpacing: '-.005em' },
  sub: { fontSize: 12, color: C.ink3 },
  num: { fontVariantNumeric: 'tabular-nums' },
  muted: { fontSize: 12.5, color: C.ink3 },
  cols: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },

  // --- cluster chips -----------------------------------------------------
  filters: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  filterLabel: {
    fontSize: 11,
    letterSpacing: '.08em',
    color: C.ink3,
    fontWeight: 600,
    marginRight: 2
  },
  chip: {
    font: 'inherit',
    fontSize: 12.5,
    cursor: 'pointer',
    border: `1px solid ${C.line}`,
    background: '#fff',
    color: C.ink2,
    padding: '5px 11px',
    borderRadius: 999
  },
  chipOn: {
    background: C.accent,
    borderColor: C.accent,
    color: '#fff',
    fontWeight: 600
  },

  // --- flow strip --------------------------------------------------------
  flowHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 12
  },
  flowTotal: {
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: '-.02em',
    lineHeight: 1,
    marginRight: 10,
    fontVariantNumeric: 'tabular-nums'
  },
  flowLabel: { fontSize: 13, color: C.ink2 },
  stamp: { fontSize: 12, color: C.ink3, fontVariantNumeric: 'tabular-nums' },
  bar: {
    display: 'flex',
    gap: 2,
    height: 34,
    width: '100%',
    borderRadius: 5,
    overflow: 'hidden',
    background: C.track
  },
  seg: {
    display: 'grid',
    placeItems: 'center',
    fontSize: 12,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  emptyLine: { fontSize: 12.5, color: C.ink3, marginTop: 10 },

  // --- legend ------------------------------------------------------------
  legend: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 2,
    marginTop: 12,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '10px 2px'
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  },
  lg: {
    font: 'inherit',
    textAlign: 'left',
    width: '100%',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderLeft: `3px solid ${C.line}`,
    padding: '10px 12px 10px 11px'
  },
  lgCount: { fontSize: 22, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-.02em' },
  lgTitle: { fontSize: 12.5, fontWeight: 600, marginTop: 2 },
  lgWho: { fontSize: 11, color: C.ink3, marginTop: 3 },
  swatch: {
    display: 'inline-block',
    width: 9,
    height: 9,
    borderRadius: 2,
    marginRight: 5,
    verticalAlign: 1
  },
  tag: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '.06em',
    padding: '2px 6px',
    borderRadius: 4,
    marginTop: 5,
    color: '#fff'
  },
  late: { color: C.crit, fontWeight: 700 },

  // --- budget meters -----------------------------------------------------
  rows: { display: 'flex', flexDirection: 'column', gap: 14 },
  meterBtn: {
    font: 'inherit',
    textAlign: 'left',
    width: '100%',
    display: 'block',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0
  },
  rowHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 10,
    fontSize: 12.5
  },
  rowName: { fontWeight: 600 },
  rowFigs: {
    color: C.ink2,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums'
  },
  rowFigsUsed: { color: C.ink, fontWeight: 700 },
  meter: {
    height: 12,
    borderRadius: 3,
    background: C.track,
    display: 'flex',
    gap: 2,
    overflow: 'hidden',
    marginTop: 5
  },
  fill: { display: 'block', height: '100%' },
  rowFoot: {
    fontSize: 11.5,
    color: C.ink3,
    marginTop: 4,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10
  },
  overTxt: { color: C.crit, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  keys: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    fontSize: 11.5,
    color: C.ink2,
    marginTop: 12
  },
  key: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  keySwatch: { width: 10, height: 10, borderRadius: 2, display: 'inline-block' },

  // --- kit readiness -----------------------------------------------------
  kit: {
    font: 'inherit',
    textAlign: 'left',
    width: '100%',
    display: 'block',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0
  },
  kitTop: { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5 },
  kitName: { fontWeight: 600 },
  kitPct: { color: C.ink2, fontVariantNumeric: 'tabular-nums' },
  kitPctDone: { color: C.good, fontWeight: 600 },
  kitTrack: {
    height: 8,
    borderRadius: 3,
    background: C.track,
    overflow: 'hidden',
    marginTop: 3
  },
  kitDetail: {
    fontSize: 11.5,
    color: C.ink3,
    marginTop: 3,
    fontVariantNumeric: 'tabular-nums'
  },

  // --- needs attention ---------------------------------------------------
  tableWrap: { overflowX: 'auto' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 12.5, minWidth: 560 },
  th: {
    textAlign: 'left',
    fontSize: 10.5,
    letterSpacing: '.08em',
    color: C.ink3,
    fontWeight: 700,
    padding: '0 10px 7px 0',
    borderBottom: `1px solid ${C.line}`
  },
  td: {
    padding: '9px 10px 9px 0',
    borderBottom: `1px solid ${C.lineSoft}`,
    verticalAlign: 'top'
  },
  tdItem: { fontWeight: 600 },
  tdMeta: { fontWeight: 400, color: C.ink3, fontSize: 11.5, display: 'block' },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    whiteSpace: 'nowrap',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 999
  },
  dot: { width: 6, height: 6, borderRadius: '50%', background: 'currentColor' },
  // The only rows on the page that do anything, so they have to look it.
  attnRow: {
    cursor: 'pointer',
    '&:hover td': { background: '#f3f6fb' },
    '&:focus': { outline: '2px solid #9db8e8', outlineOffset: -2 }
  },
  moreBtn: {
    font: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    color: C.accentInk,
    background: 'none',
    border: 'none',
    padding: '8px 0 0',
    cursor: 'pointer'
  },

  // --- category bars -----------------------------------------------------
  cat: { marginTop: 12, '&:first-child': { marginTop: 0 } },
  catTop: { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5 },
  catName: { fontWeight: 600 },
  catValue: { color: C.ink2, fontVariantNumeric: 'tabular-nums' },
  catTrack: {
    height: 9,
    background: C.track,
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 3
  },
  catFill: { display: 'block', height: '100%', background: C.r4, borderRadius: 3 }
}))

// A stage tooltip is always three lines: what it is, how much of it there is,
// and who is holding it.
const stageTip = (stage, data) => (
  <span>
    <b>{stage.tipLabel || stage.label}</b>
    <br />
    {data.count} lines · {money(data.value)}
    <br />
    {/* Only the two vendor stages ever carry an overdue count (aptFlowCounts),
        and both of them feed the chase list's "Past due from vendor" rows. */}
    {data.overdue > 0
      ? `${data.overdue} of these are past their expected date`
      : stage.detail}
  </span>
)

const FlowStrip = ({ flow }) => {
  const classes = useStyles()
  if (!flow || !flow.total) {
    return (
      <Box>
        <div className={classes.bar} />
        {flow && (
          <Typography className={classes.emptyLine}>
            Nothing open — every line is confirmed.
          </Typography>
        )}
      </Box>
    )
  }
  const aria =
    'Open lines by stage: ' +
    STAGES.map((s) => `${flow.stages[s.key].count} ${s.aria}`).join(', ') +
    '.'
  return (
    <div className={classes.bar} role="img" aria-label={aria}>
      {STAGES.map((s) => {
        const data = flow.stages[s.key]
        return (
          <Tooltip key={s.key} arrow title={stageTip(s, data)}>
            <div
              className={classes.seg}
              style={{
                flex: data.count,
                minWidth: data.count ? 34 : 0,
                background: s.color,
                color: s.onDark ? '#fff' : C.ink,
                textShadow: s.onDark ? '0 1px 2px rgba(0,0,0,.28)' : 'none'
              }}>
              {data.count || ''}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

const LegendCell = ({ stage, data, onOpen }) => {
  const classes = useStyles()
  const tone = LEGEND_TONE[stage.tone]
  return (
    <button
      type="button"
      className={classes.lg}
      title={`${stage.label} — ${data.count} open lines. Opens the ${stage.tab} tab.`}
      onClick={() => onOpen(stage.tab)}
      style={
        tone
          ? {
            borderLeftColor: tone.rail,
            background: tone.background,
            borderRadius: '0 6px 6px 0'
          }
          : undefined
      }>
      <div className={`${classes.lgCount} ${classes.num}`}>{data.count}</div>
      <div className={classes.lgTitle}>
        <span className={classes.swatch} style={{ background: stage.color }} />
        {stage.label}
      </div>
      <div className={classes.lgWho}>
        {stage.who}
        {data.overdue > 0 && (
          <span>
            {' · '}
            <span className={classes.late}>{data.overdue} late</span>
          </span>
        )}
      </div>
      {stage.tag && (
        <span className={classes.tag} style={{ background: tone.tag }}>
          {stage.tag}
        </span>
      )}
    </button>
  )
}

// What the meter says about the queue behind it. "Nothing awaiting approval"
// has to come from a COUNT of lines, never from money: a line HQ has not priced
// yet (ppp empty) carries line_total 0, so pipeline_value stays 0 while the flow
// strip on the same screen is counting those same lines as awaiting approval.
// `awaiting` is null when the request list did not load — then say only what the
// money can prove, and say nothing when it proves nothing.
const awaitingText = (row, awaiting) => {
  if (awaiting == null) {
    return row.pipeline > 0 ? `${money(row.pipeline)} awaiting approval` : ''
  }
  if (!awaiting) return 'Nothing awaiting approval'
  const label = `${awaiting} ${awaiting === 1 ? 'line' : 'lines'} awaiting approval`
  return row.pipeline > 0 ? `${label} · ${money(row.pipeline)} priced` : label
}

const BudgetMeter = ({ row, awaiting, onOpen }) => {
  const classes = useStyles()
  // Only say "opening soon" while the go-live is genuinely ahead of us.
  const soon = row.goLive && row.goLive > todayStr()
  const foot = row.over > 0 ? `Over by ${money(row.over)}` : null
  return (
    <button
      type="button"
      className={classes.meterBtn}
      title={`${row.name} — ${money(row.used)} used of ${money(
        row.budget
      )}. Opens the Budgets tab.`}
      onClick={() => onOpen('budgets')}>
      <div className={classes.rowHead}>
        <span className={classes.rowName}>{row.name || '—'}</span>
        <span className={classes.rowFigs}>
          <b className={classes.rowFigsUsed}>{money(row.used)}</b> of{' '}
          {money(row.budget)}
        </span>
      </div>
      <div className={classes.meter}>
        {row.widths.used > 0 && (
          <i
            className={classes.fill}
            style={{ width: `${row.widths.used}%`, background: C.r5 }}
          />
        )}
        {row.widths.pipeline > 0 && (
          <i
            className={classes.fill}
            style={{ width: `${row.widths.pipeline}%`, background: C.r2 }}
          />
        )}
        {row.widths.over > 0 && (
          <i
            className={classes.fill}
            style={{ width: `${row.widths.over}%`, background: C.crit }}
          />
        )}
      </div>
      <div className={classes.rowFoot}>
        <span>
          {row.cluster ? `${row.cluster} cluster` : 'No cluster'}
          {soon ? ' · opening soon' : ''}
        </span>
        {foot ? (
          <span className={classes.overTxt}>{foot}</span>
        ) : (
          <span className={classes.num}>{awaitingText(row, awaiting)}</span>
        )}
      </div>
    </button>
  )
}

const KitBar = ({ row, first, onOpen }) => {
  const classes = useStyles()
  const soon = row.goLive && row.goLive > todayStr()
  const detail = [`${row.delivered} of ${row.denominator} delivered`]
  if (row.unavailable > 0) detail.push(`${row.unavailable} unavailable`)
  // `pending` is null when the kit's request wasn't in the list we fetched —
  // say nothing rather than print a wrong 0.
  if (row.pending) detail.push(`${row.pending} not yet ordered`)
  if (soon) detail.push(`opens ${dayjs(row.goLive).format('D MMM')}`)
  return (
    <button
      type="button"
      className={classes.kit}
      style={{ marginTop: first ? 0 : 13 }}
      title={`${row.apartment} · ${row.kit} — ${row.pct}% delivered. Opens the Budgets tab.`}
      onClick={() => onOpen('budgets')}>
      <div className={classes.kitTop}>
        <span className={classes.kitName}>
          {row.apartment} · {row.kit}
        </span>
        <span
          className={`${classes.kitPct} ${row.complete ? classes.kitPctDone : ''}`}>
          {row.complete ? 'Complete' : `${row.pct}%`}
        </span>
      </div>
      <div className={classes.kitTrack}>
        <i
          className={classes.fill}
          style={{
            width: `${row.pct}%`,
            background: row.complete ? C.good : C.r4,
            borderRadius: 3
          }}
        />
      </div>
      <div className={classes.kitDetail}>{detail.join(' · ')}</div>
    </button>
  )
}

// The meta line under an item names WHY the row is here, in the words of the
// state it is in — a vendor and PO for a late line, the receiving note for a
// flagged one, the delivery date for one we owe a confirmation on.
const attentionMeta = (row) => {
  if (row.kind === 'late') {
    return [row.vendor, row.poNumber].filter(Boolean).join(' · ')
  }
  if (row.kind === 'confirm') {
    return row.deliveredOn ? `Delivered ${dayjs(row.deliveredOn).format('D MMM')}` : ''
  }
  if (row.receiptNote) return row.receiptNote
  return row.kind === 'discrepancy' ? 'Discrepancy raised' : 'Flagged not received'
}

const waitingText = (row) => {
  if (row.days == null) return '—'
  const unit = row.days === 1 ? 'day' : 'days'
  return row.kind === 'late' ? `${row.days} ${unit} late` : `${row.days} ${unit}`
}

const AttentionTable = ({ rows, more, onOpen }) => {
  const classes = useStyles()
  if (!rows.length) {
    return (
      <Typography className={classes.muted}>
        Nothing needs chasing — every line is on track.
      </Typography>
    )
  }
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.th}>Item</th>
            <th className={classes.th}>Apartment</th>
            <th className={classes.th}>Waiting</th>
            <th className={classes.th}>State</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const meta = ATTENTION_META[row.kind]
            const sub = attentionMeta(row)
            // The row names an item, an apartment, how long it has waited and
            // what it waits on — it reads as a worklist, so it behaves like
            // one. Always Requests: aptAttentionRows only emits flagged, late
            // and confirm-receipt lines, all three worked in the grid;
            // approval-stage lines are not in this list at all.
            const go = () =>
              onOpen('requests', {
                item: row.itemName || '',
                apartment: row.apartmentName || ''
              })
            return (
              <tr
                key={row.key}
                className={classes.attnRow}
                tabIndex={0}
                role="button"
                title={`Open ${row.itemName || 'this line'} in Requests`}
                onClick={go}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    go()
                  }
                }}
              >
                <td className={`${classes.td} ${classes.tdItem}`}>
                  {row.itemName || 'item'}
                  {row.qty ? ` ×${Number(row.qty)}` : ''}
                  {sub && <span className={classes.tdMeta}>{sub}</span>}
                </td>
                <td className={classes.td}>{row.apartmentName || '—'}</td>
                <td
                  className={`${classes.td} ${classes.num} ${
                    row.kind === 'late' ? classes.late : ''
                  }`}>
                  {waitingText(row)}
                </td>
                <td className={classes.td}>
                  <span
                    className={classes.pill}
                    style={{ color: meta.fg, background: meta.bg }}>
                    <span className={classes.dot} />
                    {meta.label}
                  </span>
                </td>
              </tr>
            )
          })}
          {more > 0 && (
            <tr>
              <td className={classes.td} colSpan={4}>
                <button
                  type="button"
                  className={classes.moreBtn}
                  title="Opens the Requests tab"
                  onClick={() => onOpen('requests')}>
                  +{more} more — see Requests
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const CategoryBars = ({ spend }) => {
  const classes = useStyles()
  if (!spend.rows.length) {
    return <Typography className={classes.muted}>No approved spend yet.</Typography>
  }
  return (
    <div>
      {spend.rows.map((row) => (
        <div key={row.category} className={classes.cat}>
          <div className={classes.catTop}>
            <span className={classes.catName}>{row.category}</span>
            <span className={classes.catValue}>{money(row.used)}</span>
          </div>
          <Tooltip
            arrow
            title={
              <span>
                <b>{row.category}</b>
                <br />
                {money(row.used)}
                <br />
                {row.share}% of approved spend
              </span>
            }>
            <div className={classes.catTrack}>
              <i className={classes.catFill} style={{ width: `${row.width}%` }} />
            </div>
          </Tooltip>
        </div>
      ))}
    </div>
  )
}

const DashboardTab = ({ onNavigate }) => {
  // Every hook lives above every conditional return — a hook after an early
  // return is React error #310 and blanks the whole procurement page.
  const classes = useStyles()
  // Categories added from Alfred count as budget heads, not "Other categories" —
  // the spend rollup below must recompute once the list has loaded.
  const categoryNames = useAptCategories()
  const [requests, setRequests] = React.useState(null)
  const [overview, setOverview] = React.useState(null)
  const [requestError, setRequestError] = React.useState('')
  const [overviewError, setOverviewError] = React.useState('')
  const [cluster, setCluster] = React.useState('all')
  // Stamped once on mount, not per render — the board is a snapshot, and a
  // clock that ticks invites people to treat stale counts as live ones.
  const [asOf] = React.useState(() => dayjs().format('dddd, D MMMM YYYY · HH:mm'))

  React.useEffect(() => {
    let alive = true
    fetchAptRequests({ origin: 'apartment', lines: '1' })
      .then((rows) => alive && setRequests(rows || []))
      .catch((e) => {
        if (!alive) return
        // A dead API used to read as "all clear" on the old cards — say so.
        setRequestError(apiErrorText(e))
        setRequests([])
      })
    fetchAptOverview()
      .then((rows) => alive && setOverview(rows || []))
      .catch((e) => {
        if (!alive) return
        setOverviewError(apiErrorText(e))
        setOverview([])
      })
    return () => {
      alive = false
    }
  }, [])

  const clusters = React.useMemo(() => {
    const seen = new Set()
    ;(overview || []).forEach((row) => {
      if (row.cluster) seen.add(row.cluster)
    })
    return [...seen].sort((a, b) => a.localeCompare(b))
  }, [overview])

  // One filter scopes the whole board. Requests carry cluster_name, overview
  // rows carry cluster — same value, two field names.
  const scopedRequests = React.useMemo(
    () =>
      cluster === 'all'
        ? requests || []
        : (requests || []).filter((r) => (r.cluster_name || '') === cluster),
    [requests, cluster]
  )
  const scopedOverview = React.useMemo(
    () =>
      cluster === 'all'
        ? overview || []
        : (overview || []).filter((row) => (row.cluster || '') === cluster),
    [overview, cluster]
  )

  const flow = React.useMemo(
    () => (requests ? aptFlowCounts(scopedRequests) : null),
    [requests, scopedRequests]
  )
  const budgets = React.useMemo(() => aptBudgetRows(scopedOverview), [scopedOverview])
  const kits = React.useMemo(
    () => aptKitRows(scopedOverview, requests || []),
    [scopedOverview, requests]
  )
  const attention = React.useMemo(
    () => aptAttentionRows(scopedRequests),
    [scopedRequests]
  )
  const categories = React.useMemo(
    () => aptCategorySpend(scopedOverview, scopedRequests),
    // categoryNames: read inside via aptCategoryNames(), listed so it re-runs.
    [scopedOverview, scopedRequests, categoryNames]
  )

  // The tab ids are the six literals in index.js TABS; an unknown one silently
  // bounces the user back here, so never build one.
  const nav = (id, next) => onNavigate && onNavigate(id, next)
  const overviewLoading = overview === null
  // A failed overview call leaves an EMPTY array behind, so every rollup below
  // reads as "nothing outstanding". Any card fed by it has to show the error
  // instead of its empty state, exactly as the budgets card does.
  const overviewDown = !overviewLoading && !!overviewError
  const kitTotal = kits.length
  const kitOpen = kitsIncomplete(scopedOverview, requests || [])
  // Null = we cannot count lines (the request list is missing or errored), so
  // the meters must not claim there is nothing awaiting approval.
  const awaitingByApt = flow && !requestError ? flow.awaitingByApartment : null

  return (
    <Box className={classes.page}>
      {clusters.length > 1 && (
        <Box className={classes.filters} role="group" aria-label="Filter by cluster">
          <span className={classes.filterLabel}>Cluster</span>
          <button
            type="button"
            aria-pressed={cluster === 'all'}
            title="Show every cluster"
            className={`${classes.chip} ${cluster === 'all' ? classes.chipOn : ''}`}
            onClick={() => setCluster('all')}>
            All {(overview || []).length} apartments
          </button>
          {clusters.map((name) => (
            <button
              key={name}
              type="button"
              aria-pressed={cluster === name}
              title={`Show only the ${name} cluster`}
              className={`${classes.chip} ${cluster === name ? classes.chipOn : ''}`}
              onClick={() => setCluster(name)}>
              {name}
            </button>
          ))}
        </Box>
      )}

      {/* ---- flow: one bar for the whole pipeline ------------------------ */}
      <Card variant="outlined" className={classes.card}>
        {requestError ? (
          <Typography color="error" variant="body2">
            Could not load the board — {requestError}
          </Typography>
        ) : (
          <>
            <Box className={classes.flowHead}>
              <Box>
                <span className={classes.flowTotal}>{flow ? flow.total : '—'}</span>
                <span className={classes.flowLabel}>
                  open lines · {flow ? flow.requestCount : 0} requests across{' '}
                  {flow ? flow.apartmentCount : 0} apartments
                </span>
              </Box>
              <span className={classes.stamp}>{asOf}</span>
            </Box>
            <FlowStrip flow={flow} />
            {flow && flow.total > 0 && (
              <Box className={classes.legend}>
                {STAGES.map((stage) => (
                  <LegendCell
                    key={stage.key}
                    stage={stage}
                    data={flow.stages[stage.key]}
                    onOpen={nav}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Card>

      {/* ---- budgets + kits ---------------------------------------------- */}
      <Box className={classes.cols}>
        <Card variant="outlined" className={classes.card}>
          <Box className={classes.cardHead}>
            <Typography className={classes.h2}>
              Budget used, apartment by apartment
            </Typography>
            <span className={classes.sub}>One pot per apartment · no monthly reset</span>
          </Box>
          {overviewError && (
            <Typography color="error" variant="body2">
              Could not load budgets — {overviewError}
            </Typography>
          )}
          {overviewLoading && <LinearProgress />}
          {!overviewLoading && !overviewError && !budgets.length && (
            <Typography className={classes.muted}>
              {cluster === 'all'
                ? 'No apartments yet — add them under At Apartment → Apartments.'
                : `No budget or spend recorded for the ${cluster} cluster yet.`}
            </Typography>
          )}
          {budgets.length > 0 && (
            <>
              <Box className={classes.rows}>
                {budgets.map((row) => (
                  <BudgetMeter
                    key={row.key}
                    row={row}
                    awaiting={awaitingByApt ? awaitingByApt[row.key] || 0 : null}
                    onOpen={nav}
                  />
                ))}
              </Box>
              <Box className={classes.keys}>
                {[
                  { label: 'Approved spend', color: C.r5 },
                  { label: 'Awaiting approval', color: C.r2 },
                  { label: 'Over the pot', color: C.crit },
                  { label: 'Left', color: C.track }
                ].map((k) => (
                  <span key={k.label} className={classes.key}>
                    <i className={classes.keySwatch} style={{ background: k.color }} />
                    {k.label}
                  </span>
                ))}
              </Box>
            </>
          )}
        </Card>

        <Card variant="outlined" className={classes.card}>
          <Box className={classes.cardHead}>
            <Typography className={classes.h2}>Launch kit readiness</Typography>
            <span className={classes.sub}>
              {overviewLoading || overviewDown
                ? '—'
                : `${kitOpen} of ${kitTotal} incomplete`}
            </span>
          </Box>
          {overviewLoading && <LinearProgress />}
          {overviewDown && (
            <Typography color="error" variant="body2">
              Could not load kit readiness — {overviewError}
            </Typography>
          )}
          {!overviewLoading && !overviewDown && !kitTotal && (
            <Typography className={classes.muted}>
              No launch kits generated yet — use Requests → Generate launch kit.
            </Typography>
          )}
          {kits.map((row, i) => (
            <KitBar key={row.key} row={row} first={i === 0} onOpen={nav} />
          ))}
        </Card>
      </Box>

      {/* ---- chase list + category spend --------------------------------- */}
      <Box className={classes.cols}>
        <Card variant="outlined" className={classes.card}>
          <Box className={classes.cardHead}>
            <Typography className={classes.h2}>Needs attention</Typography>
            <span className={classes.sub}>Oldest first</span>
          </Box>
          {requestError && (
            <Typography color="error" variant="body2">
              Could not load the chase list — {requestError}
            </Typography>
          )}
          {!requestError && requests === null && <LinearProgress />}
          {!requestError && requests !== null && (
            <AttentionTable rows={attention.rows} more={attention.more} onOpen={nav} />
          )}
        </Card>

        <Card variant="outlined" className={classes.card}>
          <Box className={classes.cardHead}>
            <Typography className={classes.h2}>Approved spend by category</Typography>
            <Tooltip
              arrow
              title={CATEGORY_TIP}>
              <span className={`${classes.sub} ${classes.num}`}>
                {overviewLoading || overviewDown
                  ? '—'
                  : `${money(categories.total)} total`}
              </span>
            </Tooltip>
          </Box>
          {overviewLoading && <LinearProgress />}
          {overviewDown && (
            <Typography color="error" variant="body2">
              Could not load category spend — {overviewError}
            </Typography>
          )}
          {!overviewLoading && !overviewDown && <CategoryBars spend={categories} />}
        </Card>
      </Box>
    </Box>
  )
}

export default DashboardTab
